import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, Box, Typography, IconButton, Button, Link } from '@mui/material';

const ACCENT = '#d97757';
const ACCENT_2 = '#b85a3e';
const ACCENT_SOFT = '#fbe8de';
const INK = '#0e0e10';
const INK_3 = '#6c6a64';
const INK_4 = '#9a988f';
const LINE = '#ece8df';
const LINE_2 = '#dcd6c7';

const ROLE_LABELS = {
    manager: 'Manager',
    translator: 'Translator',
    proofreader: 'Proofreader',
    viewer: 'Viewer',
};

const TIERS = [
    {
        id: 'full',
        label: 'Manager seat',
        sub: 'Internal staff — full workspace access',
        roles: ['manager'],
        billed: true,
    },
    {
        id: 'linguist',
        label: 'Linguist',
        sub: 'Translator or proofreader — assigned work only',
        roles: ['translator', 'proofreader'],
        billed: false,
    },
    {
        id: 'guest',
        label: 'Guest',
        sub: 'One project, view & comment — 30-day access',
        roles: ['viewer'],
        billed: false,
    },
];

const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

/**
 * Rush Studio "Invite people" dialog. Tier card picker + email chip input
 * + role select + optional personal message + seat-aware footer.
 * Loops POST /api/team/invite per chip so the existing single-email API
 * still works while the UI feels batched.
 */
export default function InvitePeopleDialog({
    open,
    onClose,
    token,
    teamLimits,
    onSent, // (results) => void — fires after at least one invite succeeded
    onSnackbar, // ({ open, message, severity }) => void
}) {
    const [tier, setTier] = useState('linguist');
    const [emails, setEmails] = useState([]); // [{ value, valid }]
    const [draft, setDraft] = useState('');
    const [role, setRole] = useState('translator');
    const [msg, setMsg] = useState('');
    const [showMsg, setShowMsg] = useState(false);
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [projects, setProjects] = useState([]); // [{ _id, projectName, ... }]
    const [projectId, setProjectId] = useState('');
    const [projectsLoading, setProjectsLoading] = useState(false);
    const chipRef = useRef(null);

    const tierObj = useMemo(() => TIERS.find((t) => t.id === tier) || TIERS[1], [tier]);
    const isGuest = tier === 'guest';

    useEffect(() => {
        if (!open) return;
        if (!tierObj.roles.includes(role)) setRole(tierObj.roles[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tier, open]);

    // Lazy-load the project list the first time the dialog opens with a
    // token — needed only when the user picks the Guest tier, but fetching
    // here keeps the picker instant when they switch tiers.
    useEffect(() => {
        if (!open || !token || projects.length > 0) return;
        let cancelled = false;
        setProjectsLoading(true);
        axios.get('/api/projects?summary=true', {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            if (cancelled) return;
            // The endpoint returns either { createdProjects, sharedProjects }
            // or a flat array depending on which projects.js code path runs.
            // Guests get invited to projects the inviter owns, so prefer
            // createdProjects; fall back to a flat array shape.
            const data = res.data || {};
            const list = Array.isArray(data)
                ? data
                : (data.createdProjects || []);
            setProjects(list);
        }).catch(() => { /* non-fatal — picker will show empty state */ })
            .finally(() => { if (!cancelled) setProjectsLoading(false); });
        return () => { cancelled = true; };
    }, [open, token, projects.length]);

    const reset = () => {
        setTier('linguist');
        setEmails([]);
        setDraft('');
        setRole('translator');
        setMsg('');
        setShowMsg(false);
        setSent(false);
        setProjectId('');
    };

    const close = () => {
        onClose && onClose();
        reset();
    };

    const commit = (raw) => {
        const parts = String(raw || '').split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
        if (!parts.length) return;
        setEmails((prev) => {
            const next = [...prev];
            parts.forEach((p) => {
                if (!next.some((e) => e.value.toLowerCase() === p.toLowerCase())) {
                    next.push({ value: p, valid: isValidEmail(p) });
                }
            });
            return next;
        });
        setDraft('');
    };

    const validCount = emails.filter((e) => e.valid).length;
    const hasInvalid = emails.some((e) => !e.valid);

    const seatType = tierObj.id;
    const used = teamLimits ? (seatType === 'full' ? (teamLimits.fullSeats ?? 0) : (teamLimits.linguistSeats ?? 0)) : 0;
    const limit = teamLimits ? (seatType === 'full' ? (teamLimits.fullSeatLimit ?? 0) : (teamLimits.linguistSeatLimit ?? 0)) : 0;
    const limitless = teamLimits
        ? (seatType === 'full' ? !!teamLimits.fullSeatLimitIsUnlimited : !!teamLimits.linguistSeatLimitIsUnlimited)
        : true;
    const seatsAfter = used + (seatType === 'full' ? validCount : 0);
    const overBy = limitless ? 0 : Math.max(0, seatsAfter - limit);
    const blocked = seatType === 'full' && overBy > 0;
    const guestMissingProject = isGuest && !projectId;
    const canSend = validCount > 0 && !hasInvalid && !blocked && !sending && !guestMissingProject;

    const send = async () => {
        if (!canSend) return;
        setSending(true);
        const targets = emails.filter((e) => e.valid).map((e) => e.value);
        let okCount = 0;
        let firstError = null;
        for (const email of targets) {
            try {
                const body = { email, role, message: msg || undefined };
                if (isGuest) body.projectId = projectId;
                await axios.post(
                    '/api/team/invite',
                    body,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                okCount += 1;
            } catch (err) {
                if (!firstError) firstError = err;
            }
        }
        setSending(false);
        if (okCount > 0 && !firstError) {
            setSent(true);
            onSent && onSent({ okCount, total: targets.length });
        } else if (okCount > 0) {
            onSnackbar && onSnackbar({
                open: true,
                message: `Sent ${okCount} of ${targets.length} invites. Some failed.`,
                severity: 'warning',
            });
            onSent && onSent({ okCount, total: targets.length });
        } else {
            const data = firstError?.response?.data || {};
            const message = data.code === 'FULL_SEAT_LIMIT'
                ? `${data.msg || 'Out of manager seats.'} Upgrade to add more.`
                : data.code === 'LINGUIST_LIMIT'
                    ? `${data.msg || 'Out of linguist seats.'} Upgrade to add more.`
                    : (data.msg || 'Failed to send invitations.');
            onSnackbar && onSnackbar({ open: true, message, severity: 'error' });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    bgcolor: '#fbfaf7',
                    border: `1px solid ${LINE}`,
                    boxShadow: '0 30px 80px rgba(35,18,8,0.18)',
                    overflow: 'hidden',
                },
            }}
        >
            {sent ? (
                <Box sx={{ p: '32px 30px 22px', textAlign: 'center' }}>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '50%', margin: '4px auto 14px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: ACCENT_SOFT, color: ACCENT_2,
                    }}>
                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                            <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: INK, mb: 0.75 }}>
                        {validCount} {tierObj.label.toLowerCase()}{validCount === 1 ? '' : 's'} invited
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: INK_3, lineHeight: 1.55, maxWidth: 380, mx: 'auto' }}>
                        {seatType === 'guest'
                            ? <>A magic link was sent. Guests get view + comment access to the selected project for 30 days — no account needed.</>
                            : <>Invitations are on their way. They'll appear under <b>Pending invites</b> until accepted{seatType === 'full' ? `, using ${validCount} manager seat${validCount === 1 ? '' : 's'}.` : '. Linguists don’t use a manager seat.'}</>}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, pt: 2, borderTop: `1px solid ${LINE}` }}>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            onClick={() => { setSent(false); setEmails([]); setMsg(''); setShowMsg(false); }}
                            sx={{ textTransform: 'none', color: INK, borderRadius: '10px', px: 1.75, '&:hover': { bgcolor: LINE } }}
                        >
                            Invite more
                        </Button>
                        <Button
                            onClick={close}
                            variant="contained"
                            sx={{
                                textTransform: 'none', borderRadius: '10px', px: 2.25,
                                bgcolor: ACCENT, color: '#fff', boxShadow: 'none',
                                '&:hover': { bgcolor: ACCENT_2, boxShadow: 'none' },
                            }}
                        >
                            Done
                        </Button>
                    </Box>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: '22px 26px 8px' }}>
                        <Box>
                            <Typography sx={{ fontSize: 19, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
                                Invite people
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: INK_3, mt: 0.5 }}>
                                Add teammates and linguists to your workspace.
                            </Typography>
                        </Box>
                        <IconButton onClick={close} size="small" sx={{ color: INK_3, '&:hover': { color: INK, bgcolor: LINE } }}>
                            <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
                                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                        </IconButton>
                    </Box>

                    <Box sx={{ p: '12px 26px 4px', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                        {/* Tier selector */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, mb: 1 }}>
                                Account type
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${TIERS.length}, 1fr)`, gap: 1.25 }}>
                                {TIERS.map((tt) => {
                                    const on = tier === tt.id;
                                    return (
                                        <Box
                                            key={tt.id}
                                            onClick={() => setTier(tt.id)}
                                            role="button"
                                            tabIndex={0}
                                            sx={{
                                                cursor: 'pointer',
                                                p: '14px 14px 12px',
                                                borderRadius: '14px',
                                                bgcolor: on ? ACCENT_SOFT : '#fff',
                                                border: `1.5px solid ${on ? ACCENT : LINE}`,
                                                transition: 'all .12s',
                                                boxShadow: on ? `0 0 0 3px ${ACCENT}22` : 'none',
                                                '&:hover': { borderColor: on ? ACCENT : LINE_2 },
                                            }}
                                        >
                                            <Box sx={{ color: on ? ACCENT_2 : INK, mb: 0.5 }}>
                                                {tt.id === 'full' && (
                                                    <svg viewBox="0 0 18 18" width="18" height="18" fill="none">
                                                        <circle cx="9" cy="6" r="2.7" stroke="currentColor" strokeWidth="1.5" />
                                                        <path d="M3.5 14.5c.8-2.6 2.8-4 5.5-4s4.7 1.4 5.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                    </svg>
                                                )}
                                                {tt.id === 'linguist' && (
                                                    <svg viewBox="0 0 18 18" width="18" height="18" fill="none">
                                                        <circle cx="9" cy="9" r="6.3" stroke="currentColor" strokeWidth="1.5" />
                                                        <path d="M2.7 9h12.6M9 2.7c1.7 1.6 1.7 11 0 12.6M9 2.7C7.3 4.3 7.3 13.7 9 15.3" stroke="currentColor" strokeWidth="1.5" />
                                                    </svg>
                                                )}
                                                {tt.id === 'guest' && (
                                                    <svg viewBox="0 0 18 18" width="18" height="18" fill="none">
                                                        <path d="M7.5 10.5l3-3M6 8L4.3 9.7a2.4 2.4 0 003.4 3.4L9.2 11.6M12 10l1.7-1.7a2.4 2.4 0 00-3.4-3.4L8.8 6.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{tt.label}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: INK_3, mt: 0.5, lineHeight: 1.4 }}>{tt.sub}</Typography>
                                            <Typography sx={{
                                                mt: 1, fontSize: 11, fontWeight: 600,
                                                color: tt.billed ? ACCENT_2 : '#2da06a',
                                                textTransform: 'uppercase', letterSpacing: 0.4,
                                            }}>
                                                {tt.billed ? 'Uses a member seat' : 'Free'}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Emails — chip input */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, mb: 1 }}>Email addresses</Typography>
                            <Box
                                onClick={() => chipRef.current && chipRef.current.focus()}
                                sx={{
                                    display: 'flex', flexWrap: 'wrap', gap: 0.75,
                                    minHeight: 48, p: '8px 10px',
                                    border: `1.5px solid ${LINE_2}`, borderRadius: '12px',
                                    bgcolor: '#fff', cursor: 'text',
                                    transition: 'border-color .12s, box-shadow .12s',
                                    '&:focus-within': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}22` },
                                }}
                            >
                                {emails.map((e, i) => (
                                    <Box
                                        key={`${e.value}-${i}`}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                            px: 1, py: 0.5, borderRadius: '8px',
                                            bgcolor: e.valid ? ACCENT_SOFT : '#fde2e2',
                                            color: e.valid ? ACCENT_2 : '#a8261a',
                                            fontSize: 12.5, fontWeight: 500,
                                            border: e.valid ? 'none' : '1px solid #f0b4ad',
                                        }}
                                    >
                                        {e.value}
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={(ev) => { ev.stopPropagation(); setEmails((prev) => prev.filter((_, j) => j !== i)); }}
                                            sx={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'inherit', display: 'inline-flex', p: 0, ml: 0.25 }}
                                            aria-label="Remove"
                                        >
                                            <svg viewBox="0 0 16 16" width="11" height="11" fill="none">
                                                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                            </svg>
                                        </Box>
                                    </Box>
                                ))}
                                <Box
                                    component="input"
                                    ref={chipRef}
                                    value={draft}
                                    placeholder={emails.length ? '' : 'name@company.com, paste a list…'}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                                            e.preventDefault();
                                            commit(draft);
                                        } else if (e.key === 'Backspace' && !draft && emails.length) {
                                            setEmails((prev) => prev.slice(0, -1));
                                        }
                                    }}
                                    onPaste={(e) => { e.preventDefault(); commit(e.clipboardData.getData('text')); }}
                                    onBlur={() => commit(draft)}
                                    sx={{
                                        flex: 1, minWidth: 160, border: 0, outline: 'none',
                                        bgcolor: 'transparent', fontSize: 13.5, color: INK,
                                        fontFamily: 'inherit', py: 0.5,
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontSize: 11.5, mt: 0.75, color: hasInvalid ? '#c0392b' : INK_4 }}>
                                {hasInvalid
                                    ? 'Some addresses look invalid — fix or remove the highlighted ones.'
                                    : 'Separate multiple with commas, spaces, or a pasted list.'}
                            </Typography>
                        </Box>

                        {/* Role (hidden for Guest — role is pinned to 'viewer') */}
                        {!isGuest && (
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, mb: 1 }}>Role</Typography>
                                <Box sx={{
                                    position: 'relative',
                                    border: `1.5px solid ${LINE_2}`, borderRadius: '12px', bgcolor: '#fff',
                                    '&:focus-within': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}22` },
                                }}>
                                    <Box
                                        component="select"
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                        sx={{
                                            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                                            width: '100%', border: 0, outline: 'none', bgcolor: 'transparent',
                                            p: '12px 38px 12px 14px', fontSize: 13.5, color: INK,
                                            fontFamily: 'inherit', borderRadius: '12px', cursor: 'pointer',
                                        }}
                                    >
                                        {tierObj.roles.map((r) => (
                                            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                                        ))}
                                    </Box>
                                    <Box sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: INK_4, pointerEvents: 'none' }}>
                                        <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
                                            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Project (Guest only) — one project per guest invite */}
                        {isGuest && (
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, mb: 1 }}>Project</Typography>
                                <Box sx={{
                                    position: 'relative',
                                    border: `1.5px solid ${guestMissingProject ? '#c0392b' : LINE_2}`,
                                    borderRadius: '12px', bgcolor: '#fff',
                                    '&:focus-within': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}22` },
                                }}>
                                    <Box
                                        component="select"
                                        value={projectId}
                                        onChange={(e) => setProjectId(e.target.value)}
                                        disabled={projectsLoading || projects.length === 0}
                                        sx={{
                                            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                                            width: '100%', border: 0, outline: 'none', bgcolor: 'transparent',
                                            p: '12px 38px 12px 14px', fontSize: 13.5, color: INK,
                                            fontFamily: 'inherit', borderRadius: '12px',
                                            cursor: projects.length === 0 ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        <option value="">
                                            {projectsLoading
                                                ? 'Loading projects…'
                                                : projects.length === 0
                                                    ? 'No projects available'
                                                    : 'Select a project to review…'}
                                        </option>
                                        {projects.map((p) => (
                                            <option key={p._id} value={p._id}>
                                                {p.projectName || p.title || p.name || 'Untitled project'}
                                                {p.targetLanguage ? ` — ${p.targetLanguage}` : ''}
                                            </option>
                                        ))}
                                    </Box>
                                    <Box sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: INK_4, pointerEvents: 'none' }}>
                                        <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
                                            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Box>
                                </Box>
                                <Typography sx={{
                                    fontSize: 11.5, mt: 0.75,
                                    color: guestMissingProject ? '#c0392b' : INK_4,
                                }}>
                                    {guestMissingProject
                                        ? 'Pick the project this guest should review.'
                                        : 'Guests get view + comment access to this project for 30 days.'}
                                </Typography>
                            </Box>
                        )}

                        {/* Personal message */}
                        {showMsg ? (
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK, mb: 1 }}>
                                    Personal message <Box component="span" sx={{ ml: 0.5, fontWeight: 500, color: INK_4 }}>optional</Box>
                                </Typography>
                                <Box
                                    component="textarea"
                                    rows={3}
                                    value={msg}
                                    placeholder="Add a note to the invitation email…"
                                    onChange={(e) => setMsg(e.target.value)}
                                    sx={{
                                        width: '100%', resize: 'vertical', border: `1.5px solid ${LINE_2}`,
                                        borderRadius: '12px', p: '10px 12px', fontSize: 13.5, color: INK,
                                        fontFamily: 'inherit', outline: 'none', bgcolor: '#fff',
                                        '&:focus': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}22` },
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box
                                component="button"
                                type="button"
                                onClick={() => setShowMsg(true)}
                                sx={{
                                    alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    border: 0, background: 'transparent', cursor: 'pointer',
                                    color: ACCENT_2, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
                                    p: 0, '&:hover': { color: ACCENT },
                                }}
                            >
                                <svg viewBox="0 0 12 12" width="11" height="11" fill="none">
                                    <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                </svg>
                                Add a personal message
                            </Box>
                        )}
                    </Box>

                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        p: '18px 26px 22px', mt: 1, borderTop: `1px solid ${LINE}`,
                    }}>
                        <Box sx={{ flex: 1, fontSize: 12, color: INK_3 }}>
                            {seatType === 'full' ? (
                                blocked ? (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#c0392b', fontWeight: 600 }}>
                                        {seatsAfter} of {limit} seats — {overBy} over.&nbsp;
                                        <Link href="/pricing" target="_blank" rel="noopener" sx={{ color: ACCENT_2, fontWeight: 700 }}>
                                            Add seats
                                        </Link>
                                    </Box>
                                ) : (
                                    <span>
                                        <b style={{ color: INK }}>{seatsAfter}</b> of {limitless ? '∞' : limit} manager seats used
                                        {validCount > 0 ? ` · +${validCount}` : ''}
                                    </span>
                                )
                            ) : (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#2da06a' }} />
                                    {seatType === 'guest'
                                        ? 'Free — single project, 30-day access'
                                        : "Free — doesn't use a manager seat"}
                                </Box>
                            )}
                        </Box>
                        <Button
                            onClick={close}
                            sx={{ textTransform: 'none', color: INK, borderRadius: '10px', px: 1.75, '&:hover': { bgcolor: LINE } }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={send}
                            disabled={!canSend}
                            variant="contained"
                            startIcon={
                                <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
                                    <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                                    <path d="M3 5l5 3.5L13 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            }
                            sx={{
                                textTransform: 'none', borderRadius: '10px', px: 2.25, boxShadow: 'none',
                                bgcolor: ACCENT, color: '#fff',
                                '&:hover': { bgcolor: ACCENT_2, boxShadow: 'none' },
                                '&.Mui-disabled': { bgcolor: '#e6dccf', color: '#fff' },
                            }}
                        >
                            {sending ? 'Sending…' : `Send ${validCount > 0 ? validCount + ' ' : ''}invite${validCount === 1 ? '' : 's'}`}
                        </Button>
                    </Box>
                </>
            )}
        </Dialog>
    );
}
